import React, { memo } from 'react';
import { IRoute } from 'data';
import { Spin } from 'antd';
import { Route, Switch, useHistory } from 'react-router-dom';
import './ConfigContent.scss';
import AppDetailSelector from '../components/AppDetailSelector';
import { useAppSiderWidth } from 'v2/util/hook/useAppSiderWidth';
import { RootState } from '../../../../store/reducer';
import {
  hasAnyEmptyResource,
  hasAnyFailureResource,
  hasAnyIdleLoadingResource,
  hasAnyLoadingResource,
} from '../../../util/resource/resourceUtils';
import { shallowEqual, useSelector } from 'react-redux';
import ErrorView from '../../../components/error/ErrorView';
import { t } from '../../../../helpers/i18n';
import { isProjectNotFoundError } from '../../../util/error';
import { Routes } from 'constants/routes';

interface ConfigContentProps {
  filteredRoutes: IRoute[];
}

const ConfigContent: React.FC<ConfigContentProps> = ({ filteredRoutes }) => {
  const appSiderWidth = useAppSiderWidth();
  const {
    hasError,
    isProjectNotFound,
    isLoading,
    isMissingRequiredConfig,
  } = useSelector(stateSelector, shallowEqual);
  const history = useHistory();

  if (hasError) {
    let message: string | undefined;
    let buttonText: string | undefined;
    let onRetry: (() => void) | undefined;
    if (isProjectNotFound) {
      message = t('project_config_not_found_project');
      buttonText = t('BackToHome');
      onRetry = () => {
        history.push(Routes.v2_projectList);
      };
    }

    return (
      <ErrorView
        visibleErrorImage={true}
        errorText={message}
        buttonText={buttonText}
        onButtonClick={onRetry}
      />
    );
  }

  if (isMissingRequiredConfig) {
    return (
      <ErrorView
        visibleErrorImage={true}
        errorText={t('project_config_missing_necessary_data')}
      />
    );
  }

  if (isLoading) {
    return (
      <div>
        <Spin className="app-spin" />
      </div>
    );
  }

  return (
    <>
      <div className="content-content-container">
        <div
          id="content-content-inner"
          className="content-content-inner"
          style={{ marginRight: appSiderWidth }}
        >
          <AppDetailSelector />
          <Switch>
            {filteredRoutes.map(({ component: Component, ...rest }, index) => {
              return (
                <Route
                  {...rest}
                  key={rest.path}
                  render={routeProps => {
                    return <Component {...routeProps} />;
                  }}
                />
              );
            })}
          </Switch>
        </div>
      </div>
    </>
  );
};

type SelectedState = {
  hasError: boolean;
  isProjectNotFound: boolean;
  isLoading: boolean;
  isMissingRequiredConfig: boolean;
};

const stateSelector = (rootState: RootState): SelectedState => {
  const { environmentError, platformError } = rootState.common;
  const { project, apps, appVersions, appConfigs } = rootState.projectDetail;
  const projectResources = [project, apps, appVersions, appConfigs];

  const isProjectNotFound = isProjectNotFoundError(project.error);
  const hasError =
    environmentError !== undefined ||
    platformError !== undefined ||
    hasAnyFailureResource(projectResources);

  const isLoading =
    hasAnyLoadingResource(projectResources) ||
    hasAnyIdleLoadingResource(projectResources);

  const isMissingRequiredConfig = hasAnyEmptyResource(projectResources);

  return {
    hasError,
    isProjectNotFound,
    isLoading,
    isMissingRequiredConfig,
  };
};

export default memo(ConfigContent);
